import { ethers } from 'ethers';
import { TaskEscrowABI } from '../ABI/ABI';
import { JOB_ESCROW_PAYMENT_TOKEN } from '../constant/constant';
import { readContractData } from './contractInstance';
import { waitForSuccessfulTransactionReceipt } from './transactionReceipt';

/** Jobs Diamond write function names (EIP-2535 facets). */
export const JOB_ESCROW_FN = {
    createJobEscrow: 'createJobEscrow',
    createJobAssignment: 'createJobAssignment',
    acceptJobEscrow: 'acceptJobEscrow',
    submitJobWork: 'submitJobWork',
    requestJobRework: 'requestJobRework',
    claimJobPayment: 'claimJobPayment',
    releaseJobEscrow: 'releaseJobEscrow',
    requestJobRefund: 'requestJobRefund',
    approveJobRefund: 'approveJobRefund',
    cancelJobEscrow: 'cancelJobEscrow',
    raiseJobDispute: 'raiseJobDispute',
    resolveJobDispute: 'resolveJobDispute',
    registerCompany: 'registerCompany',
    registerEmployee: 'registerEmployee',
    updateEmployeeEmploymentClass: 'updateEmployeeEmploymentClass',
    enrollAndFundPayrollEmployee: 'enrollAndFundPayrollEmployee',
    enrollPayrollEmployee: 'enrollPayrollEmployee',
    depositPayroll: 'depositPayroll',
    claimPayroll: 'claimPayroll',
};

export const EMPLOYMENT_CLASS_ON_CHAIN = {
    salaried: 1,
    freelance: 2,
};

export function employmentTypeToChainClass(employmentType) {
    const normalized = String(employmentType || 'freelance').toLowerCase();
    return (
        EMPLOYMENT_CLASS_ON_CHAIN[normalized] ?? EMPLOYMENT_CLASS_ON_CHAIN.freelance
    );
}

export async function resolveJobsPaymentToken(contractAddress) {
    const fromEnv = String(JOB_ESCROW_PAYMENT_TOKEN || '').trim();
    if (ethers.isAddress(fromEnv)) {
        return fromEnv;
    }

    try {
        const whitelisted = await readContractData(
            contractAddress,
            TaskEscrowABI,
            'getWhitelistedTokens',
            [],
        );
        if (Array.isArray(whitelisted) && whitelisted.length > 0) {
            const first = String(whitelisted[0] || '').trim();
            if (ethers.isAddress(first)) {
                return first;
            }
        }
    } catch (_error) {
        // Jobs diamond may not expose getWhitelistedTokens yet
    }

    if (ethers.isAddress(fromEnv)) {
        return fromEnv;
    }

    throw new Error(
        'Job escrow payment token is not configured. Set REACT_APP_JOB_PAYMENT_TOKEN.',
    );
}

export async function readCompanyRegistryId(contractAddress, ownerWallet) {
    const id = await readContractData(
        contractAddress,
        TaskEscrowABI,
        'getCompanyRegistryId',
        [ownerWallet],
    );
    return Number(id || 0);
}

export async function readEmployeeRegistryId(contractAddress, employeeWallet) {
    const id = await readContractData(
        contractAddress,
        TaskEscrowABI,
        'getEmployeeRegistryId',
        [employeeWallet],
    );
    return Number(id || 0);
}

export function buildCompanyProfileHash(companyId) {
    return ethers.keccak256(
        ethers.toUtf8Bytes(JSON.stringify({ companyId: String(companyId) })),
    );
}

export async function registerCompanyOnChain({
    writeContractAsync,
    contractAddress,
    walletAddress,
    chainId,
    companyId,
}) {
    const profileHash = buildCompanyProfileHash(companyId);
    const txHash = await writeContractAsync({
        address: contractAddress,
        abi: TaskEscrowABI,
        functionName: JOB_ESCROW_FN.registerCompany,
        args: [profileHash],
        chainId,
    });
    await waitForSuccessfulTransactionReceipt(txHash);
    const registryId = await readCompanyRegistryId(contractAddress, walletAddress);
    if (!registryId) {
        throw new Error('Company registration tx succeeded but registry id was not found.');
    }
    return { txHash, registryId: String(registryId) };
}

export async function registerEmployeeOnChain({
    writeContractAsync,
    contractAddress,
    chainId,
    employeeWallet,
    employmentType,
}) {
    const employmentClass = employmentTypeToChainClass(employmentType);
    const txHash = await writeContractAsync({
        address: contractAddress,
        abi: TaskEscrowABI,
        functionName: JOB_ESCROW_FN.registerEmployee,
        args: [employeeWallet, employmentClass],
        chainId,
    });
    await waitForSuccessfulTransactionReceipt(txHash);
    const registryId = await readEmployeeRegistryId(contractAddress, employeeWallet);
    if (!registryId) {
        throw new Error('Employee registration tx succeeded but registry id was not found.');
    }
    return { txHash, registryId: String(registryId) };
}

export async function readCanAssignPayrollTask(
    contractAddress,
    companyWallet,
    employeeWallet,
) {
    return Boolean(
        await readContractData(
            contractAddress,
            TaskEscrowABI,
            'canAssignPayrollTask',
            [companyWallet, employeeWallet],
        ),
    );
}

export async function readEmployeeEmploymentClass(contractAddress, employeeWallet) {
    const value = await readContractData(
        contractAddress,
        TaskEscrowABI,
        'employeeEmploymentClass',
        [employeeWallet],
    );
    return Number(value || 0);
}

export async function readPayrollEnrollmentId(
    contractAddress,
    companyWallet,
    employeeWallet,
) {
    const id = await readContractData(
        contractAddress,
        TaskEscrowABI,
        'getPayrollEnrollmentId',
        [companyWallet, employeeWallet],
    );
    const numeric = Number(id || 0);
    return numeric > 0 ? String(numeric) : '';
}

/** Human-readable reason why createJobAssignment would revert. */
export async function diagnoseSalariedAssignmentBlockers({
    contractAddress,
    companyWallet,
    employeeWallet,
}) {
    const classId = await readEmployeeEmploymentClass(
        contractAddress,
        employeeWallet,
    );
    if (classId === 0) {
        return 'Employee is not registered on-chain. Re-add the employee or sync registry.';
    }
    if (classId !== EMPLOYMENT_CLASS_ON_CHAIN.salaried) {
        return 'On-chain employment class is Freelance, not Salaried. Update class or re-register as Salaried.';
    }

    const enrollmentId = await readPayrollEnrollmentId(
        contractAddress,
        companyWallet,
        employeeWallet,
    );
    if (!enrollmentId) {
        return 'Payroll is not enrolled on-chain. Fund payroll for this salaried employee first.';
    }

    const canAssign = await readCanAssignPayrollTask(
        contractAddress,
        companyWallet,
        employeeWallet,
    );
    if (!canAssign) {
        return 'Payroll vault balance is below one monthly salary. Click Fund payroll, then assign the task.';
    }

    return '';
}

export async function updateEmployeeEmploymentClassOnChain({
    writeContractAsync,
    contractAddress,
    chainId,
    employeeRegistryId,
    employmentType,
}) {
    const employmentClass = employmentTypeToChainClass(employmentType);
    const txHash = await writeContractAsync({
        address: contractAddress,
        abi: TaskEscrowABI,
        functionName: JOB_ESCROW_FN.updateEmployeeEmploymentClass,
        args: [BigInt(String(employeeRegistryId)), employmentClass],
        chainId,
        gas: 200000n,
    });
    await waitForSuccessfulTransactionReceipt(txHash);
    return { txHash };
}

export async function enrollAndFundPayrollOnChain({
    writeContractAsync,
    contractAddress,
    chainId,
    companyWallet,
    employeeWallet,
    tokenAddress,
    amountPerPeriod,
    periodSeconds = 0,
}) {
    const txHash = await writeContractAsync({
        address: contractAddress,
        abi: TaskEscrowABI,
        functionName: JOB_ESCROW_FN.enrollAndFundPayrollEmployee,
        args: [employeeWallet, tokenAddress, amountPerPeriod, periodSeconds],
        chainId,
    });
    await waitForSuccessfulTransactionReceipt(txHash);
    const enrollmentId = await readPayrollEnrollmentId(
        contractAddress,
        companyWallet,
        employeeWallet,
    );
    if (!enrollmentId) {
        throw new Error('Payroll enrollment tx succeeded but enrollment id was not found.');
    }
    return { txHash, enrollmentId };
}

export async function depositPayrollOnChain({
    writeContractAsync,
    contractAddress,
    chainId,
    tokenAddress,
    amount,
}) {
    const txHash = await writeContractAsync({
        address: contractAddress,
        abi: TaskEscrowABI,
        functionName: JOB_ESCROW_FN.depositPayroll,
        args: [tokenAddress, amount],
        chainId,
    });
    await waitForSuccessfulTransactionReceipt(txHash);
    return { txHash };
}

export async function readPayrollEnrollment(contractAddress, enrollmentId) {
    const enrollment = await readContractData(
        contractAddress,
        TaskEscrowABI,
        'getPayrollEnrollment',
        [BigInt(String(enrollmentId))],
    );
    if (!enrollment) return null;
    return {
        company: enrollment.company ?? enrollment[0],
        employee: enrollment.employee ?? enrollment[1],
        token: enrollment.token ?? enrollment[2],
        amountPerPeriod: enrollment.amountPerPeriod ?? enrollment[3],
        periodSeconds: Number(enrollment.periodSeconds ?? enrollment[4] ?? 0),
        startTime: Number(enrollment.startTime ?? enrollment[5] ?? 0),
        lastClaimAt: Number(enrollment.lastClaimAt ?? enrollment[6] ?? 0),
        active: Boolean(enrollment.active ?? enrollment[7]),
    };
}

export async function readNextPayrollClaimAt(contractAddress, enrollmentId) {
    const value = await readContractData(
        contractAddress,
        TaskEscrowABI,
        'getNextPayrollClaimAt',
        [BigInt(String(enrollmentId))],
    );
    return Number(value || 0);
}

export async function readHasPendingPayrollClaim(contractAddress, enrollmentId) {
    return Boolean(
        await readContractData(
            contractAddress,
            TaskEscrowABI,
            'hasPendingPayrollClaim',
            [BigInt(String(enrollmentId))],
        ),
    );
}

export async function readCompanyPayrollBalance(
    contractAddress,
    companyWallet,
    tokenAddress,
) {
    const value = await readContractData(
        contractAddress,
        TaskEscrowABI,
        'companyPayrollBalance',
        [companyWallet, tokenAddress],
    );
    return value ?? 0n;
}

/**
 * Load salaried payroll status for the connected employee wallet.
 * Returns null when not enrolled / not salaried.
 */
export async function loadEmployeePayrollStatus({
    contractAddress,
    companyWallet,
    employeeWallet,
    tokenDecimals = 6,
}) {
    if (
        !ethers.isAddress(contractAddress) ||
        !ethers.isAddress(companyWallet) ||
        !ethers.isAddress(employeeWallet)
    ) {
        return null;
    }

    const classId = await readEmployeeEmploymentClass(
        contractAddress,
        employeeWallet,
    );
    if (classId !== EMPLOYMENT_CLASS_ON_CHAIN.salaried) {
        return null;
    }

    const enrollmentId = await readPayrollEnrollmentId(
        contractAddress,
        companyWallet,
        employeeWallet,
    );
    if (!enrollmentId) {
        return {
            enrolled: false,
            claimable: false,
            enrollmentId: '',
            amountPerPeriod: 0n,
            amountFormatted: '0',
            nextClaimAt: 0,
            vaultBalance: 0n,
            active: false,
        };
    }

    const [enrollment, nextClaimAt, claimable] = await Promise.all([
        readPayrollEnrollment(contractAddress, enrollmentId),
        readNextPayrollClaimAt(contractAddress, enrollmentId),
        readHasPendingPayrollClaim(contractAddress, enrollmentId),
    ]);

    if (!enrollment?.active) {
        return {
            enrolled: true,
            claimable: false,
            enrollmentId,
            amountPerPeriod: 0n,
            amountFormatted: '0',
            nextClaimAt: 0,
            vaultBalance: 0n,
            active: false,
        };
    }

    const vaultBalance = await readCompanyPayrollBalance(
        contractAddress,
        companyWallet,
        enrollment.token,
    );

    return {
        enrolled: true,
        claimable: Boolean(claimable),
        enrollmentId,
        amountPerPeriod: enrollment.amountPerPeriod,
        amountFormatted: ethers.formatUnits(
            enrollment.amountPerPeriod,
            tokenDecimals,
        ),
        nextClaimAt,
        vaultBalance,
        vaultFunded: vaultBalance >= enrollment.amountPerPeriod,
        active: true,
        periodSeconds: enrollment.periodSeconds,
        token: enrollment.token,
    };
}

export async function claimPayrollOnChain({
    writeContractAsync,
    contractAddress,
    chainId,
    enrollmentId,
}) {
    const txHash = await writeContractAsync({
        address: contractAddress,
        abi: TaskEscrowABI,
        functionName: JOB_ESCROW_FN.claimPayroll,
        args: [BigInt(String(enrollmentId))],
        chainId,
        gas: 250000n,
    });
    await waitForSuccessfulTransactionReceipt(txHash);
    return { txHash };
}

export async function createJobAssignmentOnChain({
    writeContractAsync,
    contractAddress,
    chainId,
    employeeWallet,
    metadataURI,
    metadataHash,
    dueDateTimestamp = 0,
}) {
    const txHash = await writeContractAsync({
        address: contractAddress,
        abi: TaskEscrowABI,
        functionName: JOB_ESCROW_FN.createJobAssignment,
        args: [employeeWallet, metadataURI, metadataHash, dueDateTimestamp],
        chainId,
        gas: 500000n,
    });
    return { txHash };
}

/** Map common Jobs Diamond custom errors to clear UI copy. */
export function explainJobsContractError(error) {
    const raw = String(
        error?.shortMessage ||
            error?.cause?.reason ||
            error?.cause?.shortMessage ||
            error?.details ||
            error?.message ||
            '',
    );
    const lower = raw.toLowerCase();

    if (lower.includes('employeenotfreelance')) {
        return 'This employee is Salaried on-chain. Salaried tasks cannot use per-task escrow — payroll must be funded, then assign without USDC lock.';
    }
    if (lower.includes('employeenotsalaried')) {
        return 'This employee is not Salaried on-chain. Set employment type to Salaried and sync registry before assigning a payroll task.';
    }
    if (lower.includes('payrollclaimnotready')) {
        return 'Salary is not claimable yet. Wait until the next payroll period ends.';
    }
    if (lower.includes('payrollnotenrolled')) {
        return 'Payroll is not enrolled for this employee. Use Fund payroll on the Employees page first.';
    }
    if (lower.includes('payrollnotfunded')) {
        return 'Payroll vault needs at least one month of salary locked. Use Fund payroll, then try again.';
    }
    if (lower.includes('payrollenrollmentnotfound')) {
        return 'No payroll enrollment found. Enroll and fund payroll for this salaried employee first.';
    }
    if (lower.includes('payrollenrollmentexists')) {
        return 'Payroll is already enrolled. Use Fund payroll (deposit) instead of enroll again.';
    }
    if (lower.includes('employeenotregistered')) {
        return 'Employee is not registered on the Jobs Diamond. Re-add or sync the employee on-chain.';
    }
    if (lower.includes('companynotregistered') || lower.includes('companynotactive')) {
        return 'Company is not registered (or inactive) on-chain. Complete company on-chain registration first.';
    }
    if (lower.includes('rate limit')) {
        return 'RPC provider is rate limited. Wait a minute and try again.';
    }

    return (
        error?.response?.data?.message ||
        error?.shortMessage ||
        error?.details ||
        error?.message ||
        'Request failed'
    );
}
